import {
  AUTO_GEOMETRY_RELATIONS_ENABLED,
  PointSnapStrategyType,
} from '@puppycad/cad-core';
import type {
  EditorDisplayConfig as ProjectEditorDisplayConfig,
  ProjectConfig,
  SnapSettings,
} from '@puppycad/cad-core';

/**
 * 编辑器显示配置节点键。
 */
export enum EditorProjectConfigKey {
  /**
   * 编辑器显示配置节点。
   */
  EditorDisplay = 'editorDisplay',
}

/**
 * 创建默认编辑器显示配置。
 *
 * @returns 默认编辑器显示配置。
 */
export function createDefaultEditorDisplayConfig(): ProjectEditorDisplayConfig {
  return {
    shadedSketchContoursEnabled: true,
  };
}

/**
 * 创建默认项目配置。
 *
 * @returns 默认项目配置。
 */
export function createDefaultProjectConfig(): ProjectConfig {
  return {
    [EditorProjectConfigKey.EditorDisplay]: createDefaultEditorDisplayConfig(),
  };
}

/**
 * 规范化编辑器显示配置。
 *
 * @param source 外部配置来源。
 * @returns 规范化后的显示配置。
 */
export function normalizeEditorDisplayConfig(
  source?: Partial<ProjectEditorDisplayConfig> | null,
): ProjectEditorDisplayConfig {
  return {
    ...createDefaultEditorDisplayConfig(),
    ...(source ?? {}),
  };
}

/**
 * 规范化项目配置。
 *
 * @param source 外部项目配置。
 * @returns 规范化后的项目配置。
 */
export function normalizeProjectConfig(
  source?: Partial<ProjectConfig> | null,
): ProjectConfig {
  return {
    ...createDefaultProjectConfig(),
    ...(source ?? {}),
    [EditorProjectConfigKey.EditorDisplay]: normalizeEditorDisplayConfig(
      source?.[EditorProjectConfigKey.EditorDisplay],
    ),
  };
}

/**
 * 编辑器设置树节点键。
 */
export enum SettingsTreeNodeKey {
  /**
   * 快捷键设置页节点。
   */
  KeyboardShortcuts = 'keyboard-shortcuts',
  /**
   * 草图根节点。
   */
  SketchRoot = 'sketch-root',
  /**
   * 草图下的"几何关系/捕捉"节点。
   */
  SketchRelationsAndSnap = 'sketch-relations-and-snap',
}

/**
 * 编辑器快捷键定义。
 */
export interface EditorShortcutDefinition {
  /** 快捷键分类。 */
  category: string;
  /** 快捷键组合。 */
  shortcut: string;
  /** 触发范围。 */
  scope: string;
  /** 当前行为。 */
  action: string;
}

/**
 * 编辑器已接入快捷键清单。
 *
 * 维护要求：新增键盘或鼠标修饰键交互时，必须同步维护这里。
 */
export const EDITOR_SHORTCUT_DEFINITIONS: ReadonlyArray<EditorShortcutDefinition> = [
  {
    category: '历史记录',
    shortcut: 'Ctrl/Command + Z',
    scope: '全局；草图绘制中优先作为命令内撤销',
    action: '撤销最近一次模型修改；草图绘制命令中撤销上一个绘制点。',
  },
  {
    category: '历史记录',
    shortcut: 'Ctrl/Command + Y',
    scope: '全局',
    action: '重做最近一次已撤销的模型修改。',
  },
  {
    category: '历史记录',
    shortcut: 'Ctrl/Command + Shift + Z',
    scope: '全局',
    action: '重做最近一次已撤销的模型修改。',
  },
  {
    category: '草图绘制',
    shortcut: 'Esc',
    scope: '草图、PropertyManager、插入/选择会话',
    action: '取消当前绘制、编辑或选择会话。',
  },
  {
    category: '草图绘制',
    shortcut: 'Enter',
    scope: '草图绘制中；拉伸 PropertyManager 中',
    action: '完成当前草图绘制命令；拉伸编辑会话中提交当前参数。',
  },
  {
    category: '草图绘制',
    shortcut: 'Backspace',
    scope: '草图绘制中',
    action: '撤销当前绘制命令中的上一个点，不进入全局模型历史。',
  },
  {
    category: '草图编辑',
    shortcut: 'Delete',
    scope: '草图编辑中',
    action: '删除当前选中的草图实体。',
  },
  {
    category: '选择',
    shortcut: 'Ctrl/Command + 单击',
    scope: '草图、装配体、默认选择',
    action: '切换多选状态。',
  },
  {
    category: '视图',
    shortcut: 'Ctrl + 鼠标右键拖拽',
    scope: '视口',
    action: '临时切换为平移视图。',
  },
  {
    category: '视图',
    shortcut: 'Space',
    scope: '视口',
    action: '打开视图定向器聚焦态。',
  },
  {
    category: '草图裁剪',
    shortcut: 'Shift + 拖拽',
    scope: '强力裁剪',
    action: '在强力裁剪拖拽过程中切换延伸语义。',
  },
] as const;

/**
 * 草图捕捉项展示配置。
 */
export interface SketchSnapOptionDefinition {
  /**
   * 捕捉项键，直接使用 {@link PointSnapStrategyType} 枚举值，消除转换层。
   */
  key: PointSnapStrategyType;
  /**
   * 捕捉项展示文案。
   */
  label: string;
}

/**
 * 草图捕捉项开关状态映射。
 * 使用 Partial 是因为 DynamicRuler 不在 UI 中暴露，无需在此记录。
 */
export type SketchSnapOptionState = Partial<Record<PointSnapStrategyType, boolean>>;

/**
 * 草图几何关系/捕捉设置数据，扩展自 {@link SnapSettings}，与项目持久化类型保持结构一致。
 */
export type SketchRelationsAndSnapSettings = SnapSettings;

/**
 * 草图捕捉项定义列表。
 */
export const SKETCH_SNAP_OPTION_DEFINITIONS: ReadonlyArray<SketchSnapOptionDefinition> = [
  {
    key: PointSnapStrategyType.EndpointOrigin,
    label: '端点和草图点(P)',
  },
  {
    key: PointSnapStrategyType.Center,
    label: '中心点(C)',
  },
  {
    key: PointSnapStrategyType.Midpoint,
    label: '中点(M)',
  },
  {
    key: PointSnapStrategyType.Quadrant,
    label: '象限点(Q)',
  },
  {
    key: PointSnapStrategyType.Intersection,
    label: '交叉点(I)',
  },
  {
    key: PointSnapStrategyType.Tangent,
    label: '相切(T)',
  },
  {
    key: PointSnapStrategyType.Perpendicular,
    label: '垂直(E)',
  },
  {
    key: PointSnapStrategyType.Parallel,
    label: '平行(R)',
  },
  {
    key: PointSnapStrategyType.HorizontalVertical,
    label: '水平/竖直(H)',
  },
  {
    key: PointSnapStrategyType.PointHorizontalVertical,
    label: '与点水平/竖直(V)',
  },
  {
    key: PointSnapStrategyType.Length,
    label: '长度(L)',
  },
  {
    key: PointSnapStrategyType.Grid,
    label: '网格(G)',
  },
  {
    key: PointSnapStrategyType.Angle,
    label: '角度(A)',
  },
] as const;

/**
 * 创建默认草图捕捉项状态。
 *
 * @returns 默认捕捉项状态映射。
 */
export function createDefaultSketchSnapOptionState(): SketchSnapOptionState {
  return {
    [PointSnapStrategyType.EndpointOrigin]: true,   // 默认开启：端点和草图点
    [PointSnapStrategyType.Center]: false,
    [PointSnapStrategyType.Midpoint]: false,
    [PointSnapStrategyType.Quadrant]: false,
    [PointSnapStrategyType.Intersection]: false,
    [PointSnapStrategyType.Tangent]: false,
    [PointSnapStrategyType.Perpendicular]: false,
    [PointSnapStrategyType.Parallel]: false,
    [PointSnapStrategyType.HorizontalVertical]: false,
    [PointSnapStrategyType.PointHorizontalVertical]: false,
    [PointSnapStrategyType.Length]: false,
    [PointSnapStrategyType.Grid]: false,
    [PointSnapStrategyType.Angle]: false,
  };
}

/**
 * 创建默认"几何关系/捕捉"设置。
 *
 * @returns 默认设置对象。
 */
export function createDefaultSketchRelationsAndSnapSettings(): SketchRelationsAndSnapSettings {
  return {
    isSketchSnapEnabled: true,
    isSnapToModelGeometryEnabled: false,
    isAutoGeometryRelationsEnabled: AUTO_GEOMETRY_RELATIONS_ENABLED,
    snapOptionState: createDefaultSketchSnapOptionState(),
    isGridSnapOnlyWhenGridVisibleEnabled: false,
    angleSnapDegree: 45,
  };
}
